import { useEffect, useState } from "react";
import {
  treatmentOption,
  treatmentOptionOral,
  // treatmentOptionPrescription,
  treatmentOptionTablet,
  treatmentOptionTirzepatideTablet,
} from "@/assets/images";
import { PreviewComponentConfig } from "@/pages/quiz/context/types";
import Cookies from "js-cookie";
import { CookiesKey } from "@/types/cookies";
import {
  BUNDLE_EXCLUDED_STATES,
  INJECT_EXCLUDED_STATES,
  ORAL_TREATMENT_EXCLUDED_STATES,
  TABLET_TREATMENT_EXCLUDED_STATES,
  TIRZEPATIDE_TABLET_EXCLUDED_STATES,
  TREATMENT_OPTION_ID,
} from "@/pages/quiz/context/common";

const isBundleAvailable = (state?: string) =>
  !BUNDLE_EXCLUDED_STATES.includes(state ?? "");

export const TREATMENT_OPTIONS = [
  {
    excludedStates: TIRZEPATIDE_TABLET_EXCLUDED_STATES,
    id: TREATMENT_OPTION_ID.TIRZEPATIDE_TABLET,
    isKit: false,
    isNew: false,
    image: treatmentOptionTirzepatideTablet,
    text: "The same active ingredient found in Mounjaro® & Zepbound®.",

    title: "Tirzepatide Tablets",
    // TODO: update price
    getPrice: (state: string) => {
      if (isBundleAvailable(state)) {
        return "As low as <b>$299</b> Per Month";
      }
      return "Starting at <b style='font-size: 15px'>$299</b>/First month";
    },
    getDetailPrice: (state: string) => {
      if (isBundleAvailable(state)) {
        return "As low as $299 Per Month";
      }
      return "$299/First Month";
    },
    labels: [
      {
        id: 18,
        text: "In Stock",
        textColor: "#549D66",
        bgColor: "#D1F0DB",
      },
      {
        id: 19,
        text: "Tablets",
        textColor: "#1A0053",
        bgColor: "#F3EDFF",
      },
      {
        id: 20,
        text: "Rx",
        textColor: "#FFFFFF",
        bgColor: "#4F41A6",
      },
      {
        id: 21,
        text: "FSA/HSA eligible",
        textColor: "#FFFFFF",
        bgColor: "#4F41A6",
      },
    ],
    titleDetails:
      "Experience the power of Tirzepatide — the same active ingredient found in Mounjaro® & Zepbound® — now in easy-to-take RDT tablets at a fraction of the cost. All customers also receive free expedited shipping.",
    details: [
      {
        id: 8,
        title: "Details",
        text: (
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex flex-col gap-2">
              <div className="font-bold">Licensed Pharmacies</div>
              <div>
                All of our Tirzepatide is sourced from licensed USA based
                compounding pharmacies. All batches go through stringent testing
                to ensure quality.
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-bold">Free Online Vetting</div>
              <div>
                Our nurses and providers view your intake form to ensure you
                meet the requirements for Tirzepatide. Your safety is our top
                concern.
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-bold">Personalized Plan</div>
              <div>
                After viewing your intake form our SkinnyRx nurses will offer a
                personalized dosage plan catering to your specific needs.
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-bold">24/7 Support & Care</div>
              <div>
                Our care team is available 24/7 to answer any questions or
                concerns you may have.
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 9,
        title: "Active Ingredients",
        text: (
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex flex-col gap-2">
              <div className="font-bold">Important Safety Information</div>
              <div>
                Prescription products require an online evaluation with a
                licensed medical professional who will determine if a
                prescription is appropriate. Plans are offered as a subscription
                service which can be canceled at any time. Actual product
                packaging may appear differently than shown. Physicians may
                prescribe compounded medications as needed to meet patient
                requirements or address drug shortages.
              </div>
              <div className="text-xs">
                *The FDA does not review or approve any compounded medications
                for safety or effectiveness. Prices are subject to change based
                on dosage increases.
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 10,
        title: "Why Tirzepatide Tablets?",
        text: (
          <div className="flex flex-col gap-4 mb-4">
            <div>
              Oral Tirzepatide offers a modern, convenient alternative to
              traditional injectable treatments, designed to simplify your
              weight loss and metabolic health journey.
            </div>
            <ol className="list-decimal flex flex-col gap-2 px-4">
              <li className="marker:font-bold">
                <b>Convenience:</b> For individuals who prefer to avoid
                injections, oral Tirzepatide provides a{" "}
                <b>needle-free, once-daily solution</b> that easily integrates
                into everyday routines.
              </li>
              <li className="marker:font-bold">
                <b>Accessibility:</b> The simplicity of oral dosing can support
                better consistency and adherence, making it easier for patients
                to stay on track with their treatment plan.
              </li>
              <li className="marker:font-bold">
                <b>Efficacy:</b> Tirzepatide is a next-generation medication
                known for its effectiveness in weight management and blood sugar
                control. While injectable forms are well-studied, oral
                tirzepatide represents an exciting advancement, with ongoing
                studies continuing to validate its long-term benefits.
              </li>
              <li className="marker:font-bold">
                <b>Flexibility:</b> Depending on the formulation, Tirzepatide
                tablets may be taken with or without food, allowing for a
                <b> more flexible lifestyle and dosing schedule.</b>
              </li>
            </ol>
            <div>
              Tirzepatide tablets represent a breakthrough in weight loss and
              metabolic health management. Our team is here to guide and support
              you through every step of your journey.
            </div>
          </div>
        ),
      },
    ],
  },
  {
    excludedStates: TABLET_TREATMENT_EXCLUDED_STATES,
    id: TREATMENT_OPTION_ID.TABLET,
    isKit: false,
    isNew: false,
    image: treatmentOptionTablet,
    title: "Sublingual Semaglutide Tablets",
    getPrice: (state: string) => {
      if (isBundleAvailable(state)) {
        return "As low as <b>$249</b> Per Month";
      }
      return "Starting at <b style='font-size: 15px'>$249</b>/First month";
    },
    getDetailPrice: (state: string) => {
      if (isBundleAvailable(state)) {
        return "As low as $249 Per Month";
      }
      return "$249/First Month";
    },
    text: "Semaglutide administered via tablets.",
    labels: [
      {
        id: 1,
        text: "In Stock",
        textColor: "#549D66",
        bgColor: "#D1F0DB",
      },
      {
        id: 2,
        text: "Tablets",
        textColor: "#1A0053",
        bgColor: "#F3EDFF",
      },
      {
        id: 4,
        text: "Rx",
        textColor: "#FFFFFF",
        bgColor: "#4F41A6",
      },
      {
        id: 5,
        text: "FSA/HSA eligible",
        textColor: "#FFFFFF",
        bgColor: "#4F41A6",
      },
    ],
    titleDetails:
      "Experience the power of Semaglutide—the same active ingredient found in Ozempic® and Wegovy®—now in easy-to-take RDT tablets at a fraction of the cost. All customers also receive free expedited shipping.",
    details: [
      {
        id: 8,
        title: "Details",
        text: (
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex flex-col gap-2">
              <div className="font-bold">Licensed Pharmacies</div>
              <div>
                All of our semaglutide is sourced from licensed USA based
                compounding pharmacies. All batches go through stringent testing
                to ensure quality.
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-bold">Free Online Vetting</div>
              <div>
                Our nurses and providers view your intake form to ensure you
                meet the requirements for Semaglutide. Your safety is our top
                concern.
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-bold">Personalized Plan</div>
              <div>
                After viewing your intake form our SkinnyRx nurses will offer a
                personalized dosage plan catering to your specific needs.
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-bold">24/7 Support & Care</div>
              <div>
                Our care team is available 24/7 to answer any questions or
                concerns you may have.
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 9,
        title: "Active Ingredients",
        text: (
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex flex-col gap-2">
              <div className="font-bold">Important Safety Information</div>
              <div>
                Prescription products require an online evaluation with a
                licensed medical professional who will determine if a
                prescription is appropriate. Plans are offered as a subscription
                service which can be canceled at any time. Actual product
                packaging may appear differently than shown. Physicians may
                prescribe compounded medications as needed to meet patient
                requirements or address drug shortages.
              </div>
              <div className="text-xs">
                *The FDA does not review or approve any compounded medications
                for safety or effectiveness. Prices are subject to change based
                on dosage increases.
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 10,
        title: "Why Semaglutide Tablets?",
        text: (
          <div className="flex flex-col gap-4 mb-4">
            <div>
              Oral semaglutide offers a convenient, once-daily alternative to
              traditional weekly injections.
            </div>
            <ol className="list-decimal flex flex-col gap-2 px-4">
              <li className="marker:font-bold">
                <b>Convenience:</b> Designed for individuals who prefer to avoid
                injections, oral semaglutide provides a needle-free solution
                that fits seamlessly into daily routines.
              </li>
              <li className="marker:font-bold">
                <b>Accessibility:</b> The ease of oral dosing may improve
                consistency and adherence for many patients.
              </li>
              <li className="marker:font-bold">
                <b>Efficacy:</b> While injectable semaglutide has a
                well-established clinical profile, the oral form is a newer
                option and ongoing research will further clarify its long-term
                effectiveness.
              </li>
              <li className="marker:font-bold">
                <b>Flexibility:</b> Depending on the formulation, oral
                semaglutide can often be taken with or without food, offering
                greater flexibility in lifestyle and dosing.
              </li>
            </ol>
            <div>
              Oral semaglutide represents an innovative approach to weight
              management and metabolic health. Our team is committed to
              supporting you throughout your journey, every step of the way.
            </div>
          </div>
        ),
      },
    ],
    importantSafetyInformation:
      "*Only available if prescribed after an online consultation with a healthcare provider. Benefits outlined are based on third-party studies. Plans are offered as a subscription service which can be canceled at any time. Actual product packaging may appear differently than shown. Physicians may prescribe compounded medications as needed to meet patient requirements or address drug shortages. The FDA does not review or approve any compounded medications for safety or effectiveness. Compounded Semaglutide has not been approved by the FDA for weight loss, but studies indicate potential benefits. Results may vary.",
  },
  {
    excludedStates: INJECT_EXCLUDED_STATES,
    id: TREATMENT_OPTION_ID.IS,
    isKit: false,
    image: treatmentOption,
    title: "Compounded Semaglutide",
    getPrice: (state: string) => {
      if (isBundleAvailable(state)) {
        return "As low as <b>$199</b> Per Month";
      }
      return "As low as <b>$349</b> Per Month";
    },
    getDetailPrice: (state: string) => {
      if (isBundleAvailable(state)) {
        return "As low as $199 Per Month";
      }
      return "$349 per Month";
    },
    text: "Semaglutide administered via injection.",
    labels: [
      {
        id: 6,
        text: "In Stock",
        textColor: "#549D66",
        bgColor: "#D1F0DB",
      },
      {
        id: 7,
        text: "Injection",
        textColor: "#1A0053",
        bgColor: "#F3EDFF",
      },
      {
        id: 8,
        text: "Rx",
        textColor: "#FFFFFF",
        bgColor: "#4F41A6",
      },
      {
        id: 9,
        text: "FSA/HSA eligible",
        textColor: "#FFFFFF",
        bgColor: "#4F41A6",
      },
    ],
    titleDetails:
      "Discover the power of Semaglutide the active ingredient in Ozempic® & Wegovy® for a fraction of the cost. All customers also receive free expedited shipping.",
    details: [
      {
        id: 3,
        title: "Details",
        text: (
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex flex-col gap-2">
              <div className="font-bold">Licensed Pharmacies</div>
              <div>
                All of our semaglutide is sourced from licensed USA based
                compounding pharmacies. All batches go through stringent testing
                to ensure quality.
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-bold">Free Online Vetting</div>
              <div>
                Our nurses and providers view your intake form to ensure you
                meet the requirements for Semaglutide. Your safety is our top
                concern.
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-bold">Personalized Plan</div>
              <div>
                After viewing your intake form our SkinnyRx nurses will offer a
                personalized dosage plan catering to your specific needs.
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-bold">24/7 Support & Care</div>
              <div>
                Our care team is available 24/7 to answer any questions or
                concerns you may have.
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 4,
        title: "Active Ingredients",
        text: (
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex flex-col gap-2">
              <div className="font-bold">Important Safety Information</div>
              <div>
                Prescription products require an online evaluation with a
                licensed medical professional who will determine if a
                prescription is appropriate. Plans are offered as a subscription
                service which can be canceled at any time. Actual product
                packaging may appear differently than shown. Physicians may
                prescribe compounded medications as needed to meet patient
                requirements or address drug shortages.
              </div>
              <div className="text-xs">
                *The FDA does not review or approve any compounded medications
                for safety or effectiveness. Prices are subject to change based
                on dosage increases.
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 5,
        title: "Why Injectable Semaglutide?",
        text: (
          <div className="flex flex-col gap-4 mb-4">
            <ol className="list-decimal flex flex-col gap-2 px-4">
              <li className="marker:font-bold">
                <b>Efficacy:</b> The injectable form allows for better
                absorption and bioavailability, enhancing its effectiveness in
                managing weight and blood sugar.
              </li>
              <li className="marker:font-bold">
                <b>Dosing Convenience:</b> It's typically administered once a
                week, making it easier for patients to incorporate into their
                routines compared to daily medications.
              </li>
              <li className="marker:font-bold">
                <b>Stability:</b> The injectable formulation maintains the
                stability of the active ingredient, ensuring it remains
                effective over time.
              </li>
              <li className="marker:font-bold">
                <b>Controlled Release:</b> The injection allows for a controlled
                release of the medication, providing consistent therapeutic
                effects.
              </li>
            </ol>
            <div>
              Overall injectable semaglutide is an effective method for you to
              reach your goals. Our dedicated support team will be available
              alongside you every step of the way.
            </div>
          </div>
        ),
      },
    ],
    importantSafetyInformation:
      "*Only available if prescribed after an online consultation with a healthcare provider. Benefits outlined are based on third-party studies. Plans are offered as a subscription service which can be canceled at any time. Actual product packaging may appear differently than shown. Physicians may prescribe compounded medications as needed to meet patient requirements or address drug shortages. The FDA does not review or approve any compounded medications for safety or effectiveness. Compounded Semaglutide has not been approved by the FDA for weight loss, but studies indicate potential benefits. Results may vary.",
  },
  {
    excludedStates: ORAL_TREATMENT_EXCLUDED_STATES,
    id: TREATMENT_OPTION_ID.OS,
    isKit: false,
    image: treatmentOptionOral,
    title: "Compounded Oral Semaglutide",
    getPrice: (state: string) => {
      if (isBundleAvailable(state)) {
        return "As low as <b>$199</b> Per Month";
      }
      return "Starting at <b style='font-size: 15px'>$199</b>/First month";
    },
    getDetailPrice: (state: string) => {
      if (isBundleAvailable(state)) {
        return "As low as $199 Per Month";
      }
      return "$199/First Month";
    },
    text: "Semaglutide administered via oral drops.",
    labels: [
      {
        id: 10,
        text: "In Stock",
        textColor: "#549D66",
        bgColor: "#D1F0DB",
      },
      {
        id: 11,
        text: "Oral",
        textColor: "#1A0053",
        bgColor: "#F3EDFF",
      },
      {
        id: 12,
        text: "Rx",
        textColor: "#FFFFFF",
        bgColor: "#4F41A6",
      },
      {
        id: 13,
        text: "FSA/HSA eligible",
        textColor: "#FFFFFF",
        bgColor: "#4F41A6",
      },
    ],
    titleDetails:
      "Now with no needles! Discover the power of Semaglutide the active ingredient in Ozempic® & Wegovy® for a fraction of the cost. All customers also receive free expedited shipping.",
    details: [
      {
        id: 8,
        title: "Details",
        text: (
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex flex-col gap-2">
              <div className="font-bold">Licensed Pharmacies</div>
              <div>
                All of our semaglutide is sourced from licensed USA based
                compounding pharmacies. All batches go through stringent testing
                to ensure quality.
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-bold">Free Online Vetting</div>
              <div>
                Our nurses and providers view your intake form to ensure you
                meet the requirements for Semaglutide. Your safety is our top
                concern.
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-bold">Personalized Plan</div>
              <div>
                After viewing your intake form our SkinnyRx nurses will offer a
                personalized dosage plan catering to your specific needs.
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-bold">24/7 Support & Care</div>
              <div>
                Our care team is available 24/7 to answer any questions or
                concerns you may have.
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 9,
        title: "Active Ingredients",
        text: (
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex flex-col gap-2">
              <div className="font-bold">Important Safety Information</div>
              <div>
                Prescription products require an online evaluation with a
                licensed medical professional who will determine if a
                prescription is appropriate. Plans are offered as a subscription
                service which can be canceled at any time. Actual product
                packaging may appear differently than shown. Physicians may
                prescribe compounded medications as needed to meet patient
                requirements or address drug shortages.
              </div>
              <div className="text-xs">
                *The FDA does not review or approve any compounded medications
                for safety or effectiveness. Prices are subject to change based
                on dosage increases.
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 10,
        title: "Why Oral Semaglutide?",
        text: (
          <div className="flex flex-col gap-4 mb-4">
            <div>
              Oral semaglutide is a once daily medication providing an
              alternative to weekly injections.
            </div>
            <ol className="list-decimal flex flex-col gap-2 px-4">
              <li className="marker:font-bold">
                <b>Convenience:</b> It provides a non-injection option, which
                can be more appealing for those who dislike needles or prefer
                oral medications.
              </li>
              <li className="marker:font-bold">
                <b>Accessibility:</b> Oral administration may increase adherence
                to treatment, as many people find drops easier to take
                regularly.
              </li>
              <li className="marker:font-bold">
                <b>Efficacy:</b> While injectable forms are well-established,
                oral semaglutide is newer and require more data before we know
                the efficacy.
              </li>
              <li className="marker:font-bold">
                <b>Flexibility:</b> The oral form allows for more flexibility in
                dosing and can be taken with or without food, depending on the
                specific formulation.
              </li>
            </ol>
            <div>
              Overall oral semaglutide is an effective method for you to reach
              your goals. Our dedicated support team will be available alongside
              you every step of the way.
            </div>
          </div>
        ),
      },
    ],
    importantSafetyInformation:
      "*Only available if prescribed after an online consultation with a healthcare provider. Benefits outlined are based on third-party studies. Plans are offered as a subscription service which can be canceled at any time. Actual product packaging may appear differently than shown. Physicians may prescribe compounded medications as needed to meet patient requirements or address drug shortages. The FDA does not review or approve any compounded medications for safety or effectiveness. Compounded Semaglutide has not been approved by the FDA for weight loss, but studies indicate potential benefits. Results may vary.",
  },

  // {
  //   excludedStates: [""],
  //   id: TREATMENT_OPTION_ID.KIT,
  //   isKit: true,
  //   image: treatmentOptionPrescription,
  //   title: "Oral Weight Loss Kits",
  //   getPrice: (state: string) => {
  //     if (isBundleAvailable(state)) {
  //       return "As low as <b>$115</b> Per Month";
  //     }

  //     return "Starting at <b style='font-size: 15px'>$115</b> Per month";
  //   },
  //   getDetailPrice: (state: string) => {
  //     if (isBundleAvailable(state)) {
  //       return "As low as $115 Per Month";
  //     }

  //     return "$115 Per Month";
  //   },
  //   text: "Weight loss treatment plans with oral medications.",
  //   labels: [
  //     {
  //       id: 14,
  //       text: "In Stock",
  //       textColor: "#549D66",
  //       bgColor: "#D1F0DB",
  //     },
  //     {
  //       id: 15,
  //       text: "Oral",
  //       textColor: "#1A0053",
  //       bgColor: "#F3EDFF",
  //     },
  //     {
  //       id: 16,
  //       text: "Rx",
  //       textColor: "#FFFFFF",
  //       bgColor: "#4F41A6",
  //     },
  //     {
  //       id: 17,
  //       text: "FSA/HSA eligible",
  //       textColor: "#FFFFFF",
  //       bgColor: "#4F41A6",
  //     },
  //   ],
  //   titleDetails:
  //     "Our new oral weight loss kits are custom-built for you — no needles, just effective, science-backed medication that helps curb hunger, boost metabolism, and support lasting weight loss. Personalized care, delivered to your door.",
  //   details: [
  //     {
  //       id: 8,
  //       title: "Licensed Pharmacies",
  //       text: (
  //         <div className="flex flex-col gap-4 mb-4">
  //           <div className="flex flex-col gap-2">
  //             <div>
  //               All of our medication is sourced from licensed USA based
  //               compounding pharmacies. All batches go through stringent testing
  //               to ensure quality.
  //             </div>
  //           </div>
  //         </div>
  //       ),
  //     },
  //     {
  //       id: 9,
  //       title: "Free Online Vetting",
  //       text: (
  //         <div className="flex flex-col gap-4 mb-4">
  //           <div className="flex flex-col gap-2">
  //             <div>
  //               Our nurses and providers view your intake form to ensure you
  //               meet the requirements for prescription weight loss medication.
  //               Your safety is our top concern.
  //             </div>
  //           </div>
  //         </div>
  //       ),
  //     },
  //     {
  //       id: 10,
  //       title: "What Are Oral Medication Kits?",
  //       text: (
  //         <div className="flex flex-col gap-4 mb-4">
  //           <div>
  //             Our oral weight loss kits are complete, doctor-designed programs
  //             that include prescription medication tailored to your unique
  //             needs. These medications work with your body to support safe,
  //             effective weight loss — no injections needed.
  //           </div>

  //           <p>
  //             The prescription medication inside each kit is clinically proven
  //             to:
  //           </p>
  //           <ul>
  //             <li className="mb-1">
  //               <b>Curb Hunger</b>, making it easier to stick to healthy eating
  //               habits
  //             </li>
  //             <li className="mb-1">
  //               <b>Boost your metabolism</b>, helping your body burn more
  //               calories
  //             </li>
  //             <li className="mb-1">
  //               <b>Support steady, sustainable weight loss </b>over time
  //             </li>
  //             <li className="mb-1">
  //               <b>Balance appetite hormones</b>, improving control and reducing
  //               overeating
  //             </li>
  //           </ul>
  //         </div>
  //       ),
  //     },
  //   ],
  //   importantSafetyInformation:
  //     "*Only available if prescribed after an online consultation with a healthcare provider. Benefits outlined are based on third-party studies. Plans are offered as a subscription service which can be canceled at any time. Actual product packaging may appear differently than shown. Physicians may prescribe compounded medications as needed to meet patient requirements or address drug shortages. The FDA does not review or approve any compounded medications for safety or effectiveness. Compounded Semaglutide has not been approved by the FDA for weight loss, but studies indicate potential benefits. Results may vary.",
  // },
];

export const getPreselectedProduct = () => {
  const productId = Cookies.get(CookiesKey.QUIZ_REDIRECT_PRODUCT_ID);
  if (!productId) {
    return;
  }
  return productId.toUpperCase();
};

const SecondDiv: React.FC<{ onClosePreview: () => void }> = ({
  onClosePreview,
}) => {
  return (
    <div>
      <div
        className="opacity-0 animate-fade-in mb-4"
        style={{ animationDelay: "0.5s" }}
      >
        We found <b className="text-dark-purple-2">providers</b> near you..
      </div>

      <div
        className="opacity-0 animate-fade-in mb-4"
        style={{ animationDelay: "2.5s" }}
      >
        Lose up to <b className="text-dark-purple-2">2-5 lbs</b> per week on{" "}
        <b className="text-dark-purple-2">Semaglutide</b> or{" "}
        <b className="text-dark-purple-2">Tirzepatide</b>.
      </div>

      <div
        className="flex justify-end opacity-0 animate-fade-in"
        style={{ animationDelay: "3.5s" }}
      >
        <button
          onClick={onClosePreview}
          className="text-white text-lg bg-[#362068] rounded-full px-4 py-2 font-bold mt-14 min-w-[150px]"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export const PreviewSelectTreatmentOption: React.FC<PreviewComponentConfig> = ({
  onClosePreview,
}) => {
  const [showSecondDiv, setShowSecondDiv] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSecondDiv(true);
    }, 8000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex flex-1 items-center">
      {!showSecondDiv ? (
        <div>
          <div
            className="mb-4 opacity-0 animate-fade-in"
            style={{ animationDelay: "0.5s" }}
          >
            Checking our database..
          </div>
          <div
            className="opacity-0 animate-fade-in"
            style={{ animationDelay: "2.5s" }}
          >
            We only work with{" "}
            <b className="text-dark-purple-2">
              licensed providers in your state
            </b>{" "}
            who specialize in weight loss.
          </div>

          <div
            className="flex justify-end opacity-0 animate-fade-in"
            style={{ animationDelay: "3.5s" }}
          >
            <button
              onClick={() => setShowSecondDiv(true)}
              className="text-white text-lg bg-[#362068] rounded-full px-4 py-2 font-bold mt-14 min-w-[150px]"
            >
              Next
            </button>
          </div>
        </div>
      ) : (
        <SecondDiv onClosePreview={onClosePreview} />
      )}
    </div>
  );
};
